// schemas/event/event.schema.js
import { z } from "zod";

// ============================================================================
// BASE SCHEMAS
// ============================================================================

export const TeamSchema = z.object({
  logo: z.union([z.string(), z.literal(""), z.null()]).optional(),
  team_id: z
    .union([z.string(), z.number(), z.literal(""), z.null()])
    .optional(),
  team_name: z.union([z.string(), z.literal(""), z.null()]).optional(),
});

// Location data structure as stored in Firebase
export const LocationDataSchema = z.object({
  address: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  placeId: z.string().optional(),
});

// Location structure as used in the form
export const LocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  locationData: LocationDataSchema,
  hidden: z.boolean().optional(),
});

export const MatchDetailsSchema = z.object({
  fixture_id: z.number(),
  homeTeam: TeamSchema,
  awayTeam: TeamSchema,
  event_date: z.string(),
});

export const VolunteerRequestSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  slotCount: z.union([z.string(), z.number()]),
  startTime: z.number().optional(),
  endTime: z.number().optional(),
  points: z.union([z.string(), z.number(), z.literal(""), z.null()]).optional(),
  sortIndex: z.number().optional(),
});

export const CheckInSchema = z.object({
  allowCheckin: z.union([z.boolean(), z.literal("")]).optional(),
  validateGeolocation: z.union([z.boolean(), z.literal("")]).optional(),
  season: z.union([z.string(), z.number(), z.literal(""), z.null()]).optional(),
  points: z.union([z.string(), z.number(), z.literal(""), z.null()]).optional(),
});

// RSVP schema for API format
export const RsvpSchema = z.object({
  attending: z.number().optional(),
  maybe: z.number().optional(),
  no: z.number().optional(),
  max: z.union([z.number(), z.null()]).optional(),
});

// Additional location schema as stored in Firebase
export const AdditionalLocationApiSchema = z.object({
  name: z.string().optional(),
  locationId: z.string().optional(),
  placeId: z.string().optional(),
  address: z.string().optional(),
  latitude: z.union([z.number(), z.string()]).optional(),
  longitude: z.union([z.number(), z.string()]).optional(),
});

// Volunteer request schema for API format (stored as record, id is the key)
export const VolunteerRequestApiSchema = z.object({
  title: z.string().min(1, "Title is required"),
  slotCount: z.string().min(1, "Number of slots is required"),
  points: z.union([z.string(), z.number(), z.literal(""), z.null()]).optional(),
  endTime: z.number().optional(),
  startTime: z.number().optional(),
  sortIndex: z.number().optional(),
  description: z.string().optional(),
});

// ============================================================================
// EVENT SCHEMAS
// ============================================================================

export const EventApiSchema = z.object({
  type: z.union([z.string(), z.literal("")]).optional(),
  title: z.union([z.string(), z.literal("")]).optional(),
  matchId: z.union([z.string(), z.literal("")]).optional(),
  matchName: z.union([z.string(), z.literal("")]).optional(),
  matchDate: z.union([z.number(), z.literal(""), z.null()]).optional(),
  awayTeam: TeamSchema.optional(),
  homeTeam: TeamSchema.optional(),
  isHome: z.union([z.boolean(), z.literal("")]).optional(),
  chatChannel: z.union([z.string(), z.literal("")]).optional(),
  details: z
    .object({
      description: z.union([z.string(), z.literal("")]).optional(),
      author: z.union([z.string(), z.literal("")]).optional(),
      location: z.union([z.string(), z.literal("")]).optional(),
      eventStartDate: z.union([z.number(), z.literal("")]).optional(),
      eventEndDate: z.union([z.number(), z.literal("")]).optional(),
      image: z.any().optional(),
    })
    .optional(),
  notifications: z.array(z.number()).optional(),
  showOnHomePageStart: z.union([z.number(), z.literal("")]).optional(),
  showOnHomePageEnd: z.union([z.number(), z.literal("")]).optional(),
  showForFans: z.union([z.boolean(), z.literal("")]).optional(),
  checkIn: CheckInSchema.optional(),
  volunteerRequests: z.record(z.string(), VolunteerRequestApiSchema).optional(),
  videoConferenceLink: z.union([z.string(), z.literal("")]).optional(),
  meetingCode: z.union([z.string(), z.literal("")]).optional(),
  linkedToTicket: z.union([z.boolean(), z.literal("")]).optional(),
  ticketId: z.union([z.string(), z.literal("")]).optional(),
  ticketCode: z.union([z.string(), z.literal("")]).optional(),
  ticketName: z.union([z.string(), z.literal("")]).optional(),
  ticketMatchId: z.union([z.string(), z.literal("")]).optional(),
  mapsData: AdditionalLocationApiSchema.optional(),
  additionalLocations: z.array(AdditionalLocationApiSchema).optional(),
  groups: z.array(z.string()).optional(),
  content: z
    .object({
      articleLink: z.union([z.string(), z.literal("")]).optional(),
      podcastLink: z.union([z.string(), z.literal("")]).optional(),
      videoLink: z.union([z.string(), z.literal("")]).optional(),
    })
    .optional(),
  rsvp: RsvpSchema.optional(),
  createdOn: z.union([z.number(), z.literal("")]).optional(),
});

export const EventLocalSchema = z.object({
  type: z.string(),
  eventStartDate: z.number().optional(),
  hasStartTime: z.boolean().optional(),
  title: z.string().optional(),
  additionalLocations: z.array(LocationSchema.nullable()).optional(),
  author: z.string().optional(),
  details: z.string().optional(),
  location: z.string().optional(),
  eventEndDate: z.number().optional(),
  checkIn: CheckInSchema.optional(),
  volunteerRequests: z.array(VolunteerRequestSchema).optional(),
  notificationInUtc: z.array(z.union([z.number(), z.null()])).optional(),
  showForFans: z.boolean().optional(),
  articleLink: z.string().optional(),
  podcastLink: z.string().optional(),
  videoLink: z.string().optional(),
  showOnHomePageStart: z.number().optional(),
  showOnHomePageEnd: z.number().optional(),
  matchId: z.string().optional(),
  matchName: z.string().optional(),
  isHome: z.boolean().optional(),
  awayTeam: TeamSchema.optional(),
  matchList: z.array(MatchDetailsSchema).optional(),
  clubId: z.union([z.string(), z.number()]).optional(),
  groupId: z.string().optional(),
  isLinkTicket: z.boolean().optional(),
  ticketId: z.string().optional(),
  ticketCode: z.string().optional(),
  ticketName: z.string().optional(),
  ticketMatchId: z.string().optional(),
  videoConferenceLink: z.string().optional(),
  meetingCode: z.string().optional(),
  chatChannelName: z.string().optional(),
  groups: z.array(z.string()).optional(),
  matchDate: z.number().optional(),
});
